// SB6-ANA-FILT-06 — shared title row for a filter level: the
// "L<n> · LABEL" caption + an info icon whose tooltip renders the
// Markdown copy (D11) from `infoCopy.ts`. Extracted so both the generic
// `FilterLevel` and the bespoke `KindLevel` render an identical header.
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { typography } from "@aeriosiq/ui";
import { MarkdownInfo } from "./MarkdownInfo.js";
import { InfoIcon } from "@aeriosiq/ui/icons";

interface LevelHeaderProps {
  prefix: string;
  label: string;
  /** Markdown info copy; when empty the info icon is omitted. */
  info: string;
  /** Axis key for the info-icon testid. */
  axisKey: string;
}

export function LevelHeader({
  prefix,
  label,
  info,
  axisKey,
}: LevelHeaderProps): React.ReactElement {
  return (
    <Stack direction="row" alignItems="center" spacing={0.25} sx={{ mb: 0.5 }}>
      <Typography
        component="span"
        sx={{
          fontSize: typography.size["label-md"],
          fontWeight: typography.weight.semibold,
          letterSpacing: typography.tracking["label-md"],
          color: "text.secondary",
          textTransform: "uppercase",
        }}
      >
        {prefix ? `${prefix} · ` : ""}
        {label}
      </Typography>
      {info ? (
        <Tooltip
          title={
            <Box sx={{ maxWidth: 320 }}>
              <MarkdownInfo variant="tooltip">{info}</MarkdownInfo>
            </Box>
          }
          arrow
        >
          <IconButton
            data-testid={`filter-level-info-${axisKey}`}
            aria-label={`About the ${label} filter`}
            size="small"
            sx={{
              p: 0.25,
              color: "text.disabled",
              "&:hover, &:focus-visible": { color: "primary.main" },
            }}
          >
            <InfoIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      ) : null}
    </Stack>
  );
}
